import { useState, useEffect, useRef } from 'react'
import './DateInput.css'

interface DateInputProps {
  id?: string
  value: string
  onChange: (value: string) => void
  className?: string
  placeholder?: string
}

export default function DateInput({ id, value, onChange, className = '', placeholder = 'dd/mm/yyyy' }: DateInputProps) {
  const [displayValue, setDisplayValue] = useState('')
  const datePickerRef = useRef<HTMLInputElement>(null)

  // Convert ISO date (yyyy-mm-dd) to dd/mm/yyyy for display
  useEffect(() => {
    if (value) {
      const [year, month, day] = value.split('-')
      if (year && month && day) {
        setDisplayValue(`${day}/${month}/${year}`)
      }
    } else {
      setDisplayValue('')
    }
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\D/g, '') // Remove non-digits
    let formatted = ''

    if (input.length > 0) {
      // Format as dd/mm/yyyy while typing
      if (input.length <= 2) {
        formatted = input
      } else if (input.length <= 4) {
        formatted = `${input.slice(0, 2)}/${input.slice(2)}`
      } else {
        formatted = `${input.slice(0, 2)}/${input.slice(2, 4)}/${input.slice(4, 8)}`
      }
    }

    setDisplayValue(formatted)

    // Convert to ISO format (yyyy-mm-dd) when we have a complete date
    if (input.length === 8) {
      const day = input.slice(0, 2)
      const month = input.slice(2, 4)
      const year = input.slice(4, 8)

      // Validate the date
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
      if (
        date.getDate() === parseInt(day) &&
        date.getMonth() === parseInt(month) - 1 &&
        date.getFullYear() === parseInt(year)
      ) {
        onChange(`${year}-${month}-${day}`)
      }
    } else if (input.length === 0) {
      onChange('')
    }
  }

  const handleBlur = () => {
    // If incomplete date on blur, clear it
    if (displayValue && displayValue.replace(/\D/g, '').length < 8) {
      setDisplayValue('')
      onChange('')
    }
  }

  const handleCalendarClick = () => {
    datePickerRef.current?.showPicker?.()
  }

  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isoDate = e.target.value
    if (isoDate) {
      onChange(isoDate)
    }
  }

  return (
    <div className="date-input-wrapper">
      <input
        id={id}
        type="text"
        className={className}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        maxLength={10}
      />
      <button
        type="button"
        className="calendar-button"
        onClick={handleCalendarClick}
        aria-label="Select date from calendar"
        tabIndex={-1}
      >
        📅
      </button>
      <input
        ref={datePickerRef}
        type="date"
        className="hidden-date-picker"
        value={value}
        onChange={handleDatePickerChange}
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  )
}
